import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search, Calendar, FileText, Download, Filter, CheckCircle, XCircle, Loader } from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../services/api';

const MaintenanceQueryModal = ({ isOpen, onClose, trucks }) => {
  const navigate = useNavigate();
  const [selectedTrucks, setSelectedTrucks] = useState([]);
  const [searchTruck, setSearchTruck] = useState('');
  const [dateRange, setDateRange] = useState('current_month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  // Filtrar trucks para el buscador con normalización y tolerancia de campos
  const normalizedSearch = searchTruck.trim().toLowerCase();
  const normalizeString = (str) => {
    if (!str) return '';
    try {
      return str
        .toString()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .replace(/[-_\s]/g, '');
    } catch (e) {
      return str.toString().toLowerCase().replace(/[áàäâ]/g,'a').replace(/[éèëê]/g,'e').replace(/[íìïî]/g,'i').replace(/[óòöô]/g,'o').replace(/[úùüû]/g,'u').replace(/ñ/g,'n');
    }
  };

  const normalizedSearchCompact = normalizeString(searchTruck.trim());
  // Solo mostrar resultados cuando el usuario escribe (no listar todas para evitar lentitud futura)
  const filteredTrucks = (Array.isArray(trucks) ? trucks : [])
    .filter(truck => {
      if (!normalizedSearchCompact) return false; // no mostrar nada si búsqueda vacía
      const econo = normalizeString(truck.numero_economico || truck.NumeroEconomico || truck.numeroEconomico || truck.economic_number || truck.unit_number || '');
      const placas = normalizeString(truck.placas || truck.Placas || '');
      const tipo = normalizeString(truck.tipo_unidad || truck.tipoUnidad || truck.TipoUnidad || '');
      const marca = normalizeString(truck.marca || '');
      const modelo = normalizeString(truck.modelo || '');
      return (
        econo.includes(normalizedSearchCompact) ||
        placas.includes(normalizedSearchCompact) ||
        tipo.includes(normalizedSearchCompact) ||
        marca.includes(normalizedSearchCompact) ||
        modelo.includes(normalizedSearchCompact)
      );
    })
    .slice(0, 30); // limitar a 30 coincidencias para rendimiento

  // Agregar truck a la selección
  const handleAddTruck = (truck) => {
    if (!selectedTrucks.find(t => t.id === truck.id)) {
      setSelectedTrucks([...selectedTrucks, truck]);
    }
    setSearchTruck('');
  };

  // Remover truck de la selección
  const handleRemoveTruck = (truckId) => {
    setSelectedTrucks(selectedTrucks.filter(t => t.id !== truckId));
  };

  // Calcular fechas según el rango seleccionado
  const getDateRange = () => {
    const today = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case 'current_month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'last_month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'last_2_months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        endDate = today;
        break;
      case 'last_3_months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        endDate = today;
        break;
      case 'current_year':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = today;
        break;
      case 'all_time':
        startDate = new Date(2000, 0, 1);
        endDate = today;
        break;
      case 'custom':
        startDate = customStartDate ? new Date(customStartDate) : null;
        endDate = customEndDate ? new Date(customEndDate) : null;
        break;
      default:
        startDate = null;
        endDate = null;
    }

    return { startDate, endDate };
  };

  // Buscar mantenimientos
  const handleSearch = async () => {
    if (selectedTrucks.length === 0) {
      alert('Selecciona al menos una unidad');
      return;
    }

    const { startDate, endDate } = getDateRange();
    
    if (!startDate || !endDate) {
      alert('Selecciona un rango de fechas válido');
      return;
    }

    setLoading(true);
    setShowResults(false);

    try {
      const truckIds = selectedTrucks.map(t => t.id);
      
      // Consultar mantenimientos para cada truck
      const allMaintenances = [];
      
      for (const truckId of truckIds) {
        try {
          const response = await api.get(`/maintenances/vehicle/${truckId}`);
          const data = response.data;
        
        // La respuesta es un array directamente
        if (Array.isArray(data)) {
          // Filtrar por rango de fechas
          const filtered = data.filter(m => {
            const maintenanceDate = new Date(m.FechaDeMantenimiento || m.maintenance_date);
            return maintenanceDate >= startDate && maintenanceDate <= endDate;
          });
          
          allMaintenances.push(...filtered);
        }
        } catch (error) {
          console.error(`Error al obtener mantenimientos del vehículo ${truckId}:`, error);
        }
      }

      // Ordenar por fecha (más recientes primero)
      allMaintenances.sort((a, b) => {
        const dateA = new Date(a.FechaDeMantenimiento || a.maintenance_date);
        const dateB = new Date(b.FechaDeMantenimiento || b.maintenance_date);
        return dateB - dateA;
      });

      setResults(allMaintenances);
      setShowResults(true);
    } catch (error) {
      console.error('Error buscando mantenimientos:', error);
      alert('Error al buscar mantenimientos');
    } finally {
      setLoading(false);
    }
  };

  // Exportar a Excel
  const handleExportExcel = () => {
    if (results.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    // Exportar columnas alineadas con la tabla actual (ID, Unidad, Fecha, Tipo, Serie, PDF)
    const exportData = results.map(m => {
      const date = new Date(m.FechaDeMantenimiento || m.maintenance_date);
      const userTimezoneOffset = date.getTimezoneOffset() * 60000;
      const localDate = new Date(date.getTime() + userTimezoneOffset);
      
      return {
        'ID': m.IDMantenimiento || m.id || 'N/A',
        'Unidad': m.NumeroEconomico || m.numero_economico || 'N/A',
        'Fecha': localDate.toLocaleDateString('es-MX'),
        'Tipo': m.TipoDeMantenimiento || m.maintenance_type || 'N/A',
        'Serie': m.Serie || m.serie || 'N/A',
        'PDF': (m.PDFPath || m.pdf_path) ? 'Sí' : 'No'
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Mantenimientos');

    // Ajustar ancho de columnas
    const colWidths = [
      { wch: 10 }, // ID
      { wch: 15 }, // Unidad
      { wch: 12 }, // Fecha
      { wch: 18 }, // Tipo
      { wch: 18 }, // Serie
      { wch: 6 }   // PDF
    ];
    ws['!cols'] = colWidths;

    const fileName = `Mantenimientos_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Resetear formulario
  const handleReset = () => {
    setSelectedTrucks([]);
    setSearchTruck('');
    setDateRange('current_month');
    setCustomStartDate('');
    setCustomEndDate('');
    setResults([]);
    setShowResults(false);
  };

  // Descargar PDF sin abrir nueva pestaña
  const handleDownloadPdf = async (e, maintenance) => {
    e.stopPropagation();
    const id = maintenance.IDMantenimiento || maintenance.id;
    if (!id) return;
    try {
      const res = await api.get(`/maintenances/${id}/pdf-documento`, { responseType: 'blob' });
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Mantenimiento_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      console.error('Error descargando PDF', err);
      alert('Error descargando PDF');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Filter className="w-6 h-6" />
            <h2 className="text-xl font-bold">Consulta Masiva de Mantenimientos</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {!showResults ? (
            <>
              {/* Selección de Unidades */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Seleccionar Unidades
                </label>
                
                {/* Buscador */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    value={searchTruck}
                    onChange={(e) => setSearchTruck(e.target.value)}
                    placeholder="Buscar por número de unidad o económico..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Lista de búsqueda */}
                {searchTruck && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg max-h-64 overflow-y-auto mb-3">
                    {filteredTrucks.length > 0 ? (
                      filteredTrucks.map(truck => (
                        <button
                          key={truck.id}
                          onClick={() => handleAddTruck(truck)}
                          className="w-full text-left px-4 py-2 hover:bg-blue-50 transition flex justify-between items-center"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{truck.numero_economico || truck.NumeroEconomico || truck.numeroEconomico}</span>
                            <span className="text-xs text-gray-500">Placas: {truck.placas || '—'} | Tipo: {truck.tipo_unidad || '—'} | Marca: {truck.marca || '—'} | Modelo: {truck.modelo || '—'}</span>
                          </div>
                          <span className="text-xs text-gray-400">ID: {truck.id}</span>
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-gray-500 text-center">
                        {Array.isArray(trucks) && trucks.length === 0 ? 'Cargando unidades...' : 'No se encontraron coincidencias con el texto ingresado'}
                      </div>
                    )}
                  </div>
                )}

                {/* Unidades seleccionadas */}
                <div className="flex flex-wrap gap-2">
                  {selectedTrucks.map(truck => (
                    <div
                      key={truck.id}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center gap-2"
                    >
                      <span className="font-medium">{truck.numero_economico}</span>
                      <button
                        onClick={() => handleRemoveTruck(truck.id)}
                        className="hover:bg-blue-200 rounded-full p-1 transition"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {selectedTrucks.length === 0 && (
                    <span className="text-gray-400 text-sm">No hay unidades seleccionadas</span>
                  )}
                </div>
              </div>

              {/* Rango de Fechas */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Rango de Fechas
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
                  {[
                    { value: 'current_month', label: 'Mes Actual' },
                    { value: 'last_month', label: 'Mes Pasado' },
                    { value: 'last_2_months', label: 'Últimos 2 Meses' },
                    { value: 'last_3_months', label: 'Últimos 3 Meses' },
                    { value: 'current_year', label: 'Año Actual' },
                    { value: 'all_time', label: 'Todo el Historial' },
                    { value: 'custom', label: 'Personalizado' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setDateRange(option.value)}
                      className={`px-4 py-2 rounded-lg border transition ${
                        dateRange === option.value
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                {/* Fechas personalizadas */}
                {dateRange === 'custom' && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Fecha Inicio</label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Fecha Fin</label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Botones de acción */}
              <div className="flex gap-3">
                <button
                  onClick={handleSearch}
                  disabled={loading || selectedTrucks.length === 0}
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      <span>Buscando...</span>
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5" />
                      <span>Buscar Mantenimientos</span>
                    </>
                  )}
                </button>
                <button
                  onClick={handleReset}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                >
                  Limpiar
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Resultados */}
              <div className="mb-4 flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">
                    Resultados: {results.length} mantenimiento{results.length !== 1 ? 's' : ''}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {selectedTrucks.length} unidad{selectedTrucks.length !== 1 ? 'es' : ''} consultada{selectedTrucks.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleExportExcel}
                    className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
                  >
                    <Download className="w-5 h-5" />
                    <span>Exportar Excel</span>
                  </button>
                  <button
                    onClick={() => setShowResults(false)}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  >
                    Nueva Consulta
                  </button>
                </div>
              </div>

              {/* Tabla de resultados */}
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unidad</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serie</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PDF</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {results.map((maintenance, index) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          const id = maintenance.IDMantenimiento || maintenance.id;
                          if (id) {
                            navigate(`/maintenances/${id}`);
                          }
                        }}
                        title="Ver detalle"
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {maintenance.IDMantenimiento || maintenance.id || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
                          {maintenance.NumeroEconomico || maintenance.numero_economico || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                          {(() => {
                            const date = new Date(maintenance.FechaDeMantenimiento || maintenance.maintenance_date);
                            const userTimezoneOffset = date.getTimezoneOffset() * 60000;
                            const localDate = new Date(date.getTime() + userTimezoneOffset);
                            return localDate.toLocaleDateString('es-MX');
                          })()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {maintenance.TipoDeMantenimiento || maintenance.maintenance_type || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                          {maintenance.Serie || maintenance.serie || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {maintenance.PDFPath || maintenance.pdf_path ? (
                            <div className="flex items-center gap-2 text-green-600">
                              <CheckCircle className="w-5 h-5" />
                              <span className="text-sm font-medium">Sí</span>
                              <button
                                type="button"
                                onClick={(e) => handleDownloadPdf(e, maintenance)}
                                title="Descargar PDF"
                                className="text-blue-600 hover:text-blue-800 p-1 rounded"
                              >
                                <Download className="w-5 h-5" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-red-500">
                              <XCircle className="w-5 h-5" />
                              <span className="text-sm font-medium">No</span>
                            </div>
                          )}
                        </td>
                        {/* Columnas Costo y Estado eliminadas según requerimiento */}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {results.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No se encontraron mantenimientos</p>
                  <p className="text-sm">Intenta con otro rango de fechas o unidades</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaintenanceQueryModal;
